import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal'
import { useShowLocationModal } from 'context/general/ShowLocationModal';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import eventsApi from 'api/requests/events';
import colors from 'theme/colors';
import { Check, Globe, MapPin } from 'lucide-react';


export default function ModalLocation({ onChangeModalLocation = () => false }) {
    const { currentLocation, setCurrentLocation } = useCurrentLocation();
    const { showLocationModal, setShowLocationModal } = useShowLocationModal();
    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false)
    const [locations, setLocations] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const searchInputRef = useRef(null)

    useEffect(() => {
        getActiveLocations();
    }, []);

    useEffect(() => {
        if (showLocationModal && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 300);
        }
        if (!showLocationModal) {
            setSearchTerm('');
        }
    }, [showLocationModal]);

    // Esc fecha. O componente Modal usado aqui não trata teclado, e um seletor
    // que só sai por clique no X trava quem navega sem mouse.
    useEffect(() => {
        if (!showLocationModal) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowLocationModal(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showLocationModal, setShowLocationModal]);

    async function getActiveLocations() {
        setLoading(true);
        try {
            const { data: res } = await eventsApi.getActiveLocations();
            setLocations(res?.states || []);
        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    }

    const filteredLocations = useMemo(() => {
        const base = !searchTerm.trim()
            ? locations
            : (() => {
                const term = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

                return locations
                    .map((state) => {
                        const stateName = state.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const stateMatches = stateName.includes(term);

                        const filteredCities = state.cities?.filter((city) => {
                            const cityName = city.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                            return cityName.includes(term);
                        });

                        if (stateMatches) return state;
                        if (filteredCities?.length > 0) return { ...state, cities: filteredCities };
                        return null;
                    })
                    .filter(Boolean);
            })();

        // O estado da cidade atual vem primeiro. Antes a ordem era alfab\u00e9tica por
        // estado, ent\u00e3o quem est\u00e1 em Pelotas abria a modal e via Alagoas no topo,
        // tendo de rolar at\u00e9 encontrar a pr\u00f3pria regi\u00e3o.
        //
        // A compara\u00e7\u00e3o \u00e9 pela cidade, n\u00e3o por `currentLocation.state`: o valor
        // persistido \u00e9 apenas `{ city }` quando a localiza\u00e7\u00e3o n\u00e3o veio da sele\u00e7\u00e3o
        // manual, e ordenar pelo c\u00f3digo do estado n\u00e3o pegaria esse caso.
        if (!currentLocation?.city) {
            return base;
        }

        const isCurrentState = (state) =>
            state.code === currentLocation.state
            || state.cities?.some(city => city.name === currentLocation.city);

        return [...base].sort((a, b) => {
            const aCurrent = isCurrentState(a);
            const bCurrent = isCurrentState(b);

            if (aCurrent === bCurrent) return 0;
            return aCurrent ? -1 : 1;
        });
    }, [searchTerm, locations, currentLocation?.state, currentLocation?.city]);

    const resultsCount = useMemo(
        () => filteredLocations.reduce((total, state) => total + (state.cities?.length || 0), 0),
        [filteredLocations]
    );

    // Enter escolhe o primeiro resultado: com a busca preenchida, obrigar o
    // usu\u00e1rio a tirar a m\u00e3o do teclado para clicar num chip quebra o fluxo.
    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }

        const firstState = filteredLocations[0];
        const firstCity = firstState?.cities?.[0];

        if (firstCity) {
            onChangeSelect(firstCity.name, firstState.code);
        }
    }

    function onChangeSelect(cityName, stateCode) {
        setCurrentLocation({ city: cityName, state: stateCode });
        setShowLocationModal(false);
        onChangeModalLocation({ city: cityName, state: stateCode });
        setSearchParams(new URLSearchParams());
    }

    function handleClose() {
        setShowLocationModal(false);
    }

    return (
        <Modal
            isOpen={showLocationModal}
            onClose={handleClose}
            childrenOverflowY='scroll'
            closeButton={false}
        >
            <ModalWrapper>
                <ModalHeader>
                    <CloseButton onClick={handleClose} aria-label="Fechar">
                        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                            <path d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z" fill="currentColor" />
                        </svg>
                    </CloseButton>

                    <HeaderTitle>Escolha sua cidade</HeaderTitle>

                    <SearchWrapper>
                        <SearchSvg />
                        <SearchInput
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar cidade ou estado..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            aria-label="Buscar cidade ou estado"
                        />
                        {searchTerm && (
                            <ClearButton
                                onClick={() => setSearchTerm('')}
                                aria-label="Limpar busca"
                            >
                                <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                    <path d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z" fill="currentColor" />
                                </svg>
                            </ClearButton>
                        )}
                    </SearchWrapper>
                </ModalHeader>

                <ContentLocation role="listbox" aria-label="Cidades disponíveis">
                    {!searchTerm && (
                        <ScopeRow
                            type="button"
                            $active={!currentLocation?.city}
                            role="option"
                            aria-selected={!currentLocation?.city}
                            onClick={() => {
                                setCurrentLocation(null);
                                setShowLocationModal(false);
                                const params = new URLSearchParams(window.location.search);
                                if (params.has('city')) {
                                    params.delete('city');
                                    const newSearch = params.toString();
                                    navigate(window.location.pathname + (newSearch ? `?${newSearch}` : ''));
                                }
                            }}
                        >
                            <ScopeIcon $active={!currentLocation?.city}>
                                <Globe size={17} />
                            </ScopeIcon>

                            <ScopeText>
                                <ScopeTitle>Todas as cidades</ScopeTitle>
                                <ScopeHint>Ver eventos de todo o Brasil</ScopeHint>
                            </ScopeText>

                            {!currentLocation?.city && <Check size={17} color={colors.occasionalPurple} />}
                        </ScopeRow>
                    )}

                    {/* Contagem dos resultados: sem ela a busca filtra em silêncio e
                        não fica claro se o termo achou pouco ou se a lista é curta. */}
                    {searchTerm && !loading && resultsCount > 0 && (
                        <ResultsCount>
                            {resultsCount} {resultsCount === 1 ? 'cidade encontrada' : 'cidades encontradas'}
                        </ResultsCount>
                    )}

                    {loading ? (
                        <LoaderWrapper>
                            <Loader size="medium" />
                        </LoaderWrapper>
                    ) : filteredLocations.length === 0 ? (
                        <EmptyState>
                            <EmptyIcon>
                                <MapPin size={22} color={colors.night} />
                            </EmptyIcon>
                            <EmptyText>Nenhuma cidade encontrada para “{searchTerm}”</EmptyText>
                            <EmptyAction type="button" onClick={() => setSearchTerm('')}>
                                Ver todas as cidades
                            </EmptyAction>
                        </EmptyState>
                    ) : (
                        filteredLocations.map((state, stateIndex) => (
                            <StateGroup key={state.code} $index={stateIndex}>
                                <StateName>{state.name}</StateName>

                                <CitiesGrid>
                                    {state.cities?.map((city) => {
                                        const isSelected = currentLocation?.city === city.name;
                                        return (
                                            <CityChip
                                                key={`${state.code}-${city.name}`}
                                                $selected={isSelected}
                                                onClick={() => onChangeSelect(city.name, state.code)}
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                {isSelected && <Check size={14} strokeWidth={3} />}
                                                {city.name}
                                            </CityChip>
                                        );
                                    })}
                                </CitiesGrid>
                            </StateGroup>
                        ))
                    )}
                </ContentLocation>
            </ModalWrapper>
        </Modal>
    )
}

function SearchSvg() {
    return (
        <SearchIconWrapper>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M14.2939 12.5786H13.3905L13.0703 12.2699C14.191 10.9663 14.8656 9.27387 14.8656 7.43282C14.8656 3.32762 11.538 0 7.43282 0C3.32762 0 0 3.32762 0 7.43282C0 11.538 3.32762 14.8656 7.43282 14.8656C9.27387 14.8656 10.9663 14.191 12.2699 13.0703L12.5786 13.3905V14.2939L18.2962 20L20 18.2962L14.2939 12.5786ZM7.43282 12.5786C4.58548 12.5786 2.28702 10.2802 2.28702 7.43282C2.28702 4.58548 4.58548 2.28702 7.43282 2.28702C10.2802 2.28702 12.5786 4.58548 12.5786 7.43282C12.5786 10.2802 10.2802 12.5786 7.43282 12.5786Z" fill="currentColor" />
            </svg>
        </SearchIconWrapper>
    );
}


/* ============ Animations ============ */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

/* ============ Styled Components ============ */

const ModalWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* O container do Modal centraliza o texto por padrão, o que deixava os
       rótulos de estado centralizados sobre chips alinhados à esquerda. */
    text-align: left;
`;

/* Header fixo: a lista rola sob ele, então o campo de busca continua ao alcance
   em vez de sumir junto com o conteúdo. A borda inferior só aparece quando há
   algo rolado por baixo. */
const ModalHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 4px 0 14px;
    text-align: center;
    flex-shrink: 0;
    background: ${colors.neutralLight};
    border-bottom: 1px solid rgba(26, 26, 26, 0.07);

    /* O título continua centralizado; o alinhamento à esquerda vale para a
       lista, onde os rótulos precisam acompanhar os chips. */
    & > h2 {
        text-align: center;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`;

const HeaderTitle = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 16px;
`;

const SearchWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const SearchIconWrapper = styled.div`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.nightLight};
    display: flex;
    align-items: center;
    pointer-events: none;
    transition: color 0.2s ease;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 44px;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 24px;
    padding: 0 40px 0 40px;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralBackground};
    outline: none;
    transition: all 0.2s ease;
    font-family: inherit;

    &::placeholder {
        color: ${colors.nightLight};
    }

    &:focus {
        border-color: ${colors.occasionalPurple};
        background: ${colors.neutralLight};
        box-shadow: 0 0 0 3px rgba(159, 22, 219, 0.08);
    }
`;

const ClearButton = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: none;
    background: ${colors.neutralDark};
    color: ${colors.night};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
    }
`;

const ContentLocation = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    padding: 14px 0 4px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
`;

const StateGroup = styled.div`
    width: 100%;
    animation: ${fadeIn} 0.3s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 0.05, 0.25)}s;

    & + & {
        margin-top: 20px;
    }
`;

/* nightLight (#ACACAC) dava 2,27:1 sobre branco — abaixo do mínimo AA e
   ilegível justamente no rótulo que organiza a lista inteira. */
const StateName = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
`;

const CitiesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const CityChip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 15px;
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    cursor: pointer;
    transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
    font-family: inherit;
    white-space: nowrap;

    &:hover {
        border-color: ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
        background: #FAF5FF;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }

    ${({ $selected }) =>
        $selected &&
        css`
            border-color: ${colors.occasionalPurple};
            background: #F3EAFF;
            color: ${colors.occasionalPurple};

            &:hover {
                background: #EADCFF;
                border-color: ${colors.occasionalPurple};
                color: ${colors.occasionalPurple};
            }
        `}
`;

/* Era uma linha com emoji 🌍 e texto roxo, destoando de tudo em volta e sem
   mostrar quando estava ativa. Virou uma opção de escopo legível, com ícone do
   mesmo conjunto do resto da interface e marca de selecionada. */
const ScopeRow = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    margin-bottom: 16px;
    border-radius: 12px;
    border: 1px solid ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $active }) => $active ? '#FAF5FF' : colors.neutralLight};
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: border-color 0.16s ease, background 0.16s ease;

    &:hover {
        border-color: ${colors.occasionalPurple};
        background: #FAF5FF;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const ScopeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    flex-shrink: 0;
    background: ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    color: ${({ $active }) => $active ? colors.neutralLight : colors.night};
`;

const ScopeText = styled.span`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;

const ScopeTitle = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const ScopeHint = styled.span`
    font-size: 12px;
    color: ${colors.night};
    margin-top: 1px;
`;

const ResultsCount = styled.p`
    margin: 0 0 12px;
    font-size: 12px;
    color: ${colors.night};
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: ${colors.neutralDark};
    margin-bottom: 12px;
`;

const EmptyText = styled.p`
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.night};
    margin: 0;
    max-width: 260px;
`;

/* O estado vazio antes era um beco: a mensagem não oferecia saída e o usuário
   precisava apagar o termo manualmente. */
const EmptyAction = styled.button`
    margin-top: 14px;
    padding: 9px 16px;
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;

    &:hover {
        background: #FAF5FF;
        border-color: ${colors.occasionalPurple};
    }
`;
